import { ANSEL } from '@somnhold/content';
import { dist2, iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, Structure, UnsleepingState } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { at, by, crowd, heroesNear, hurt, isTower, said, standing, type Kit } from './kit.ts';
import { shoot, stood, trackStill } from './ranged.ts';

/*
 * Ansel, the Ash Scholar (mage): he burned the library so the nightmares would not have it. His shots burst on the
 * heroes around his target; every few seconds his burnt pages cover the most wounded nightmare near him in ash; a slow
 * ember chases a hero (two from his second phase) and bursts on it, or on a tower it touches, leaving it burning; a
 * wall of fire rises across the way, burning whoever stands in it and silencing the towers by it; and the library
 * burns — columns of fire on the tightest towers (five in his last phase), which lose most of their health and burn
 * silent. In his last phase a ring of ash burns whoever stands still near him.
 */

// cds: pages, ember, wall. k: the ring's pulse counter.
const PAGES = 0;
const EMBER = 1;
const WALL = 2;

/** Burnt pages: ash over the most wounded nightmare near him. */
const pages = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const p = ANSEL.pages;
  let best: Enemy | null = null;
  let bs = 0;
  for (const o of s.enemies) {
    if (o === e || o.hp <= 0 || o.un || o.kind === 'boneWall' || !withinRange(e.x, e.y, o.x, o.y, p.radius)) continue;
    const share = idiv(o.hp * 10000, imax(1, o.maxHp));
    if (!best || share < bs) {
      best = o;
      bs = share;
    }
  }
  u.cds[PAGES] = best ? p.every : 12;
  if (!best) return;
  best.ash = mulBp(p.ash, u.dmgBp);
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: best.x, ty: best.y, kind: 'anselPages' });
};

/** Wandering ember: at the nearest hero in reach (two embers at two heroes from his second phase). */
const ember = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const m = ANSEL.ember;
  const near = s.heroes
    .filter((h) => standing(h) && withinRange(e.x, e.y, h.x, h.y, m.range))
    .sort((a, b) => (a.pid === u.hunt ? 0 : 1) - (b.pid === u.hunt ? 0 : 1) || dist2(e.x, e.y, a.x, a.y) - dist2(e.x, e.y, b.x, b.y) || a.id - b.id);
  if (!near.length) return false;
  const n = e.phase >= 1 ? 2 : 1;
  for (let i = 0; i < n; i++) {
    const h = near[i % near.length] as Hero;
    if (i > 0 && h === near[0]) break;
    u.orbs.push({ x: e.x, y: e.y, tgt: h.id, ticks: m.ticks });
  }
  u.cds[EMBER] = m.every;
  e.facing = iatan2((near[0] as Hero).y - e.y, (near[0] as Hero).x - e.x);
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Ember');
  return true;
};

/** An ember bursts where it is. */
const burst = (s: BattleState, e: Enemy, u: UnsleepingState, x: number, y: number, tower: Structure | null): void => {
  const m = ANSEL.ember;
  for (const h of heroesNear(s, x, y, m.radius)) hurt(s, e, u, h, m.damage, m.maxHpBp, 'fire');
  if (tower && !tower.destroyed) {
    tower.burnTicks = imax(tower.burnTicks ?? 0, m.burn.ticks);
    tower.burnDmg = imax(tower.burnDmg ?? 0, mulBp(m.burn.dmg, u.dmgBp));
  }
  ev(s, { t: 'boom', x, y, r: m.radius, kind: 'anselEmber' });
};

/** The embers fly toward their heroes; each bursts on reaching one, touching a tower, or burning out. */
const embers = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  if (!u.orbs.length) return;
  const m = ANSEL.ember;
  const keep: UnsleepingState['orbs'] = [];
  for (const o of u.orbs) {
    const h = s.heroes.find((x) => x.id === o.tgt);
    if (h && standing(h)) {
      const dx = h.x - o.x;
      const dy = h.y - o.y;
      const d = isqrt(dx * dx + dy * dy);
      if (d <= m.step) {
        o.x = h.x;
        o.y = h.y;
      } else {
        o.x += idiv(dx * m.step, d);
        o.y += idiv(dy * m.step, d);
      }
    }
    o.ticks--;
    if (heroesNear(s, o.x, o.y, m.hit).length) {
      burst(s, e, u, o.x, o.y, null);
      continue;
    }
    const t = s.structures.find((st) => isTower(st) && withinRange(o.x, o.y, at(st).x, at(st).y, m.touch));
    if (t) {
      burst(s, e, u, o.x, o.y, t);
      continue;
    }
    if (o.ticks <= 0) {
      burst(s, e, u, o.x, o.y, null);
      continue;
    }
    keep.push(o);
  }
  u.orbs = keep;
};

/** Ash wall: across the way between him and the tightest heroes in reach. */
const wall = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const w = ANSEL.wall;
  const c = crowd(s, e, w.range, w.spacing * 2);
  if (!c) return false;
  const dx = c.x - e.x;
  const dy = c.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const along = imax(w.near, idiv(d, 2));
  const cx = e.x + idiv(dx * along, d);
  const cy = e.y + idiv(dy * along, d);
  for (let i = 0; i < w.cells; i++) {
    // Offsets of half a spacing either side of the middle: -2.5 … 2.5 spacings for six fires.
    const off = idiv((2 * i - (w.cells - 1)) * w.spacing, 2);
    s.zones.push({ id: s.nextId++, kind: 'anselWall', x: cx + idiv(-dy * off, d), y: cy + idiv(dx * off, d), radius: w.radius, cells: [], ticks: w.ticks, every: w.pulse, damage: w.damage, slowBp: 0, pid: '' });
  }
  u.cds[WALL] = w.every;
  e.facing = iatan2(dy, dx);
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'beam', x: cx + idiv(dy * w.spacing * 3, d), y: cy - idiv(dx * w.spacing * 3, d), tx: cx - idiv(dy * w.spacing * 3, d), ty: cy + idiv(dx * w.spacing * 3, d), kind: 'anselWall' });
  said(s, e, u, 'Wall', cx, cy);
  return true;
};

/** His fires on the ground each tick: the wall burns the heroes in it and silences the towers by it; the ring in his last phase. */
const ground = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const w = ANSEL.wall;
  for (const z of s.zones) {
    if (z.kind !== 'anselWall') continue;
    if (z.ticks % z.every === 0) for (const h of heroesNear(s, z.x, z.y, z.radius)) hurt(s, e, u, h, z.damage, w.maxHpBp, 'fire');
    for (const st of s.structures) {
      if (!isTower(st)) continue;
      const c = at(st);
      if (withinRange(z.x, z.y, c.x, c.y, w.silence)) st.dozeUntil = imax(st.dozeUntil ?? 0, s.tick + 2);
    }
  }
  if (e.phase < 2 || e.hp <= 0) return;
  const r = ANSEL.ring;
  u.k[0] = ((u.k[0] ?? 0) + 1) % r.pulse;
  if (u.k[0] !== 0) return;
  for (const h of heroesNear(s, e.x, e.y, r.radius)) if (stood(h, r.still)) hurt(s, e, u, h, r.damage, r.maxHpBp, 'fire');
  ev(s, { t: 'boom', x: e.x, y: e.y, r: r.radius, kind: 'anselRing' });
};

/** The `n` towers within `range` of him that stand closest together (the one with the tightest neighbours, and those). */
const tightest = (s: BattleState, e: Enemy, range: number, n: number): Structure[] => {
  const ts = s.structures.filter((st) => isTower(st) && withinRange(e.x, e.y, at(st).x, at(st).y, range));
  if (ts.length <= n) return ts;
  let best: Structure[] = [];
  let bs = 0;
  for (const t of ts) {
    const c = at(t);
    const by2 = ts.filter((o) => o !== t).sort((a, b) => dist2(c.x, c.y, at(a).x, at(a).y) - dist2(c.x, c.y, at(b).x, at(b).y) || a.id - b.id);
    const pick = by2.slice(0, n - 1);
    let sum = 0;
    for (const o of pick) sum += isqrt(dist2(c.x, c.y, at(o).x, at(o).y));
    if (!best.length || sum < bs) {
      best = [t, ...pick];
      bs = sum;
    }
  }
  return best;
};

export const ansel: Kit = {
  init: (_s, _e, u) => {
    u.cds = [ANSEL.pages.every >> 1, ANSEL.ember.every >> 1, ANSEL.wall.every >> 1];
    u.k = [0];
  },
  act: (s, e, u) => {
    if (u.cds[PAGES] === 0) pages(s, e, u);
    if (u.cds[EMBER] === 0 && ember(s, e, u)) return true;
    if (u.cds[WALL] === 0 && wall(s, e, u)) return true;
    return shoot(s, e, u, ANSEL.range, 0x7fffffff, 'anselShot', ansel.strike);
  },
  // Columns on the tightest towers in reach; with none, on the heroes.
  aim: (s, e, u) => {
    const l = ANSEL.library;
    const n = l.towers[e.phase] ?? 3;
    const ts = tightest(s, e, l.range, n);
    if (ts.length) {
      u.spots = ts.map((t) => ({ x: at(t).x, y: at(t).y, r: l.radius }));
      u.tgts = ts.map((t) => t.id);
    } else {
      const hs = s.heroes.filter((h) => standing(h) && withinRange(e.x, e.y, h.x, h.y, l.range)).slice(0, n);
      if (!hs.length) return false;
      u.spots = hs.map((h) => ({ x: h.x, y: h.y, r: l.radius }));
      u.tgts = [];
    }
    const f = u.spots[0] as { x: number; y: number };
    u.castA = iatan2(f.y - e.y, f.x - e.x);
    said(s, e, u, 'Library', f.x, f.y);
    return true;
  },
  land: (s, e, u) => {
    const l = ANSEL.library;
    for (const p of u.spots) {
      for (const st of s.structures) {
        if (!isTower(st) || !withinRange(p.x, p.y, at(st).x, at(st).y, p.r)) continue;
        damageStructure(s, st, mulBp(st.maxHp, l.towerBp), e, 'fire');
        if (st.destroyed) continue;
        st.burnTicks = imax(st.burnTicks ?? 0, l.ticks);
        st.burnDmg = imax(st.burnDmg ?? 0, mulBp(l.burnDmg, u.dmgBp));
        st.dozeUntil = imax(st.dozeUntil ?? 0, s.tick + l.ticks);
      }
      for (const h of heroesNear(s, p.x, p.y, p.r)) hurt(s, e, u, h, l.damage, l.maxHpBp, 'fire');
      ev(s, { t: 'boom', x: p.x, y: p.y, r: p.r, kind: 'anselColumn' });
    }
    u.tgts = [];
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 1500, kind: 'anselLibrary' });
  },
  // His shots burst: the target and every hero around it.
  strike: (s, e, h, dmg) => {
    damageHero(s, h, by(h, dmg, ANSEL.blowMaxHpBp), e, 'fire', true);
    for (const o of s.heroes) {
      if (o === h || !standing(o) || !withinRange(h.x, h.y, o.x, o.y, ANSEL.splash)) continue;
      damageHero(s, o, by(o, dmg, ANSEL.blowMaxHpBp), e, 'fire', true);
    }
    ev(s, { t: 'boom', x: h.x, y: h.y, r: ANSEL.splash, kind: 'anselBurst' });
  },
  status: (s, e, u) => {
    trackStill(s);
    embers(s, e, u);
    ground(s, e, u);
  },
};
